import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { renameResult, getResultCsvUrl, type ApiConvResult } from '../../lib/chatApi'
import { EditableTitle } from './EditableTitle'
import { normalizeColumns, normalizeRows } from '../../components/ui/resultGrid/normalizers'
import { useActiveWorkspace } from '../../lib/workspace'

const PREVIEW_ROWS = 8

function DownloadCsvLink({ workspaceKey, resultId, title }: { workspaceKey: string; resultId: string; title: string }) {
  return (
    <a
      href={getResultCsvUrl(workspaceKey, resultId)}
      download={`${title || 'result'}.csv`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
    >
      <Download size={13} />
      CSV
    </a>
  )
}

// Preview card → opens the full artifact detail page (OLD behaviour).
export function ResultTile({ result }: { result: ApiConvResult }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { activeWorkspaceKey } = useActiveWorkspace()

  const headers = normalizeColumns(result.columns)
  const allRows = normalizeRows(result.rows, headers)
  const previewRows = allRows.slice(0, PREVIEW_ROWS)
  const hiddenRows = allRows.length - previewRows.length

  const openDetail = () => navigate({ to: '/artifacts/$kind/$id', params: { kind: 'result', id: result.id } })

  const patchCache = (title: string) => {
    queryClient.setQueriesData<ApiConvResult[] | undefined>(
      { queryKey: ['allResults'] },
      (old) => old?.map((r) => (r.id === result.id ? { ...r, title } : r)),
    )
  }

  return (
    <div
      data-testid="artifact-tile"
      data-artifact-kind="result"
      data-artifact-id={result.id}
      className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col"
    >
      <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center justify-center gap-1.5 px-2">
        <EditableTitle
          value={result.title}
          canEdit={true}
          trigger="doubleClick"
          className="block truncate"
          inputClassName="w-full bg-white border border-gray-300 rounded px-1.5 py-0.5 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-400 text-center"
          ariaLabel="Rename result"
          onSave={async (next) => {
            const prev = result.title
            patchCache(next)
            try {
              await renameResult(activeWorkspaceKey, result.id, next)
              queryClient.invalidateQueries({ queryKey: ['conversationResults'] })
            } catch (e) {
              patchCache(prev)
              throw e
            }
          }}
        />
      </div>
      <button
        type="button"
        onClick={openDetail}
        aria-label={`Open ${result.title}`}
        className="aspect-[16/9] w-full bg-gray-50 rounded border border-gray-100 flex flex-col overflow-hidden text-left cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
      >
        {headers.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-xs text-gray-400">No data</div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden">
              <table className="w-full text-xs border-collapse table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    {headers.map((h) => (
                      <th key={h} className="px-2 py-1 text-left text-gray-600 font-medium border-b border-gray-200 truncate">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {headers.map((_, j) => (
                        <td key={j} className="px-2 py-1 text-gray-700 truncate">{row[j] == null ? '' : String(row[j])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hiddenRows > 0 && (
              <div className="text-center text-[10px] text-gray-500 py-1 border-t border-gray-100 bg-white">
                … +{hiddenRows} more row{hiddenRows === 1 ? '' : 's'}
              </div>
            )}
          </>
        )}
      </button>
      <div className="flex items-center justify-center gap-1.5 mt-3">
        <DownloadCsvLink workspaceKey={activeWorkspaceKey} resultId={result.id} title={result.title} />
      </div>
    </div>
  )
}
